import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Loader from './Components/Loader';

export default function ProtectedRoute({ children, allowOnboarding = false }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          if (mounted) {
            setUser(null);
            setLoading(false);
          }
          return;
        }

        if (mounted) setUser(session.user);

        // Check personal table status
        const { data: personal } = await supabase
          .from('personal')
          .select('status')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (mounted) {
          setStatus(personal?.status || 'onboarding');
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        if (mounted) setLoading(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setUser(null);
        setStatus(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#1a1b1e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Loader size={80} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // No status or onboarding = must complete personal info
  if (!status || status === 'onboarding') {
    if (allowOnboarding) {
      return children; // They're on /personal-info, allow it
    }
    // Trying to access protected page but incomplete = show message then redirect
    return <Navigate to="/personal-info" state={{ message: 'Please complete your signup first' }} replace />;
  }

  // Active status = allow through
  if (status === 'active') {
    return children;
  }

  // Fallback
  return <Navigate to="/login" replace />;
}